import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { AlertProvider } from './contexts/AlertContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';

// Lazy load pages for better performance
// const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const Services = React.lazy(() => import('./pages/Services'));
const ServiceForm = React.lazy(() => import('./pages/ServiceForm'));
const Appointments = React.lazy(() => import('./pages/Appointments'));
const Analytics = React.lazy(() => import('./pages/Analytics'));
const Settings = React.lazy(() => import('./pages/Settings'));
const WidgetSettings = React.lazy(() => import('./pages/WidgetSettings'));
const PaymentSettings = React.lazy(() => import('./pages/PaymentSettings'));
const IntegrationSettings = React.lazy(() => import('./pages/IntegrationSettings'));
const EmailSettings = React.lazy(() => import('./pages/EmailSettings'));
const BusinessHoursSettings = React.lazy(() => import('./pages/BusinessHoursSettings'));
const EmbedCode = React.lazy(() => import('./pages/EmbedCode'));
const Categories = React.lazy(() => import('./pages/Categories'));
const CategoryForm = React.lazy(() => import('./pages/CategoryForm'));
const ProductItems = React.lazy(() => import('./pages/ProductItems'));
const ProductItemForm = React.lazy(() => import('./pages/ProductItemForm'));
const DeliveryZones = React.lazy(() => import('./pages/DeliveryZones'));
const ProductOrders = React.lazy(() => import('./pages/ProductOrders'));
const Customers = React.lazy(() => import('./pages/Customers'));
const CustomerTransactions = React.lazy(() => import('./pages/CustomerTransactions'));
const ConsultationLinks = React.lazy(() => import('./pages/ConsultationLinks'));
// const Orders = React.lazy(() => import('./pages/Orders'));
const Login = React.lazy(() => import('./pages/Login'));
const Register = React.lazy(() => import('./pages/Register'));

const PageLoader: React.FC = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brand-500"></div>
  </div>
);

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AlertProvider>
          <React.Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />

              {/* Protected routes */}
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Analytics />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              {/* <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Dashboard />
                    </Layout>
                  </ProtectedRoute>
                }
              /> */}

              {/* Services routes */}
              <Route
                path="/services"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Services />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/services/new"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ServiceForm />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/services/:id/edit"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ServiceForm />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Categories routes */}
              <Route
                path="/categories"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Categories />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/categories/new"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <CategoryForm />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/categories/:id/edit"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <CategoryForm />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Products routes */}
              <Route
                path="/products"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ProductItems />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/products/new"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ProductItemForm />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/products/:id/edit"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ProductItemForm />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/products/delivery-zones"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <DeliveryZones />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/products/orders"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ProductOrders />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Appointments route */}
              <Route
                path="/appointments"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Appointments />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Customers routes */}
              <Route
                path="/customers"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Customers />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/customers/:id/transactions"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <CustomerTransactions />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Orders route */}
              {/* <Route
                path="/orders"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Orders />
                    </Layout>
                  </ProtectedRoute>
                }
              /> */}

              {/* Analytics route */}
              <Route
                path="/analytics"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Analytics />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Settings routes with nested tabs */}
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Settings />
                    </Layout>
                  </ProtectedRoute>
                }
              >
                {/* Default redirect to business hours settings */}
                <Route index element={<Navigate to="/settings/business-hours" replace />} />
                <Route path="business-hours" element={<BusinessHoursSettings />} />
                <Route path="widget" element={<WidgetSettings />} />
                <Route path="payment" element={<PaymentSettings />} />
                <Route path="integrations" element={<IntegrationSettings />} />
                <Route path="email" element={<EmailSettings />} />
              </Route>

              {/* Consultation Links route */}
              <Route
                path="/consultation-links"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ConsultationLinks />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Embed Code route */}
              <Route
                path="/embed-code"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <EmbedCode />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Catch all - redirect to analytics */}
              <Route path="*" element={<Navigate to="/analytics" replace />} />
            </Routes>
          </React.Suspense>
        </AlertProvider>
      </AuthProvider>
    </BrowserRouter>
  );
};

export default App;
